// [NEW FILE] Shared logic for the public e-mail link pages (backend migration 011 "link exchange sessions").
//
// Used by: pages/auth/ResetPasswordPage.jsx and pages/auth/AcceptInvitationPage.jsx
//
// Flow when the person opens the link from the e-mail ( /reset-password?token=XXXX ):
//   1. GET  .../validate?token=XXXX   -> read-only check, gives a clean "link expired" screen
//   2. POST .../exchange {token}      -> backend returns { exchange_token, expires_in }
//                                        and makes the e-mail token unusable
//   3. The ?token=XXXX is removed from the address bar (history.replaceState), so it is not kept
//      in browser history or sent to other sites in the Referer header.
//   4. The page submits the new password with the exchange token.
// The exchange token is kept in sessionStorage (this tab only) so a page refresh still works
// until it expires (LINK_EXCHANGE_TTL_MINUTES in the backend, default 10 minutes).
//
// If the backend is older and has no validate/exchange endpoints (404/405), the hook falls back
// to the original behaviour and uses the e-mail token directly.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const TOKEN_LENGTH = 43;
const MISSING = [404, 405, 501];

// React.StrictMode runs effects twice in development. The exchange is one-time only, so the
// second run must reuse the first request instead of calling the backend again.
const inFlight = new Map();

const storageKey = (purpose) => `pms_link_exchange_${purpose}`;

function readStored(purpose) {
  try {
    const raw = sessionStorage.getItem(storageKey(purpose));
    if (!raw) return null;
    const value = JSON.parse(raw);
    if (!value?.token || !value?.expiresAt || value.expiresAt <= Date.now()) {
      sessionStorage.removeItem(storageKey(purpose));
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

function writeStored(purpose, value) {
  try { sessionStorage.setItem(storageKey(purpose), JSON.stringify(value)); } catch { /* private mode: keep in memory only */ }
}

export function clearLinkToken(purpose) {
  try { sessionStorage.removeItem(storageKey(purpose)); } catch { /* ignore */ }
}

async function prepare(rawToken, validate, exchange) {
  try {
    await validate(rawToken);
  } catch (err) {
    if (!MISSING.includes(err?.response?.status)) throw err;
    return { token: rawToken, expiresAt: null, legacy: true };
  }
  try {
    const { data } = await exchange(rawToken);
    return { token: data.exchange_token, expiresAt: Date.now() + Number(data.expires_in || 600) * 1000, legacy: false };
  } catch (err) {
    if (!MISSING.includes(err?.response?.status)) throw err;
    return { token: rawToken, expiresAt: null, legacy: true };
  }
}

/**
 * @param {"reset"|"invite"} purpose
 * @param {{ validate: (token) => Promise, exchange: (token) => Promise, invalidCodes: string[] }} api
 * @returns {{ status: "checking"|"ready"|"invalid"|"error", token: string, expiresAt: number|null, secondsLeft: number|null, retry: () => void }}
 */
export default function useEmailLinkToken(purpose, { validate, exchange, invalidCodes = [] }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlToken = (searchParams.get("token") || "").trim();
  const [state, setState] = useState(() => {
    if (!urlToken) {
      const stored = readStored(purpose);
      return stored ? { status: "ready", ...stored } : { status: "invalid", token: "", expiresAt: null };
    }
    return { status: urlToken.length === TOKEN_LENGTH ? "checking" : "invalid", token: "", expiresAt: null };
  });
  const [attempt, setAttempt] = useState(0);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!urlToken || urlToken.length !== TOKEN_LENGTH) return undefined;
    let cancelled = false;
    const key = `${purpose}:${urlToken}`;
    if (!inFlight.has(key)) inFlight.set(key, prepare(urlToken, validate, exchange));
    inFlight.get(key)
      .then((result) => {
        if (cancelled) return;
        if (!result.legacy) {
          writeStored(purpose, { token: result.token, expiresAt: result.expiresAt });
          // Remove ?token= from the address bar; the e-mail token is no longer valid anyway.
          const next = new URLSearchParams(searchParams);
          next.delete("token");
          setSearchParams(next, { replace: true });
        }
        setNow(Date.now());
        setState({ status: "ready", token: result.token, expiresAt: result.expiresAt });
      })
      .catch((err) => {
        inFlight.delete(key);
        if (cancelled) return;
        const code = err?.response?.data?.error?.code;
        const status = err?.response?.status;
        if (invalidCodes.includes(code) || status === 400 || status === 410 || status === 422) {
          setState({ status: "invalid", token: "", expiresAt: null });
        } else {
          setState({ status: "error", token: "", expiresAt: null, message: err?.response ? (err.response.data?.error?.message || "") : "Unable to reach the server. Check your connection and try again." });
        }
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlToken, purpose, attempt]);

  // Ticking clock for the "finish within X minutes" note.
  useEffect(() => {
    if (state.status !== "ready" || !state.expiresAt) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [state.status, state.expiresAt]);

  const retry = useCallback(() => {
    setState((s) => ({ ...s, status: "checking" }));
    setAttempt((n) => n + 1);
  }, []);

  const secondsLeft = state.expiresAt ? Math.max(0, Math.ceil((state.expiresAt - now) / 1000)) : null;
  return { ...state, secondsLeft, retry };
}

export const formatSeconds = (secs) => `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
